import { InvoiceLineItem, InvoiceSortValue } from "@common/types/invoice";
import { InvoiceLineItemColumn } from "@common/types/views/invoiceViews";
import { Ledger } from "@common/contracts/ledger.contract";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ViewConfig } from "@lib/views";
import {
    buildLedgerAccountColumnIdByName,
    buildLedgerReceivableColumnIdByName,
    uniqueLedgersByBasisAndName,
} from "@lib/ledgerViewColumns";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
    SCHEDULE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { DateCell } from "./cells/DateCell";
import { LockCell } from "./cells/LockCell";
import { StatusCell } from "./cells/StatusCell";
import { ItemCell } from "./cells/ItemCell";
import { KindCell } from "./cells/KindCell";
import { CustomerCell } from "./cells/CustomerCell";
import { AccountCell } from "./cells/AccountCell";
import { AccrualLedgerCell } from "./cells/AccrualLedgerCell";
import { AccountsReceivableCell } from "./cells/AccountsReceivableCell";
import { AmountCell } from "./cells/AmountCell";
import { OpenAmountCell } from "./cells/OpenAmountCell";
import { EntityCell } from "./cells/EntityCell";
import { ExternalLinkCell } from "./cells/ExternalLinkCell";
import { DescriptionCell } from "./cells/DescriptionCell";
import { ScheduleCell } from "./cells/ScheduleCell";
import { MatchCell } from "./cells/MatchCell";
import { ClassesCell } from "./cells/ClassesCell";
import { InvoiceNumberCell } from "./cells/InvoiceNumberCell";

interface CreateInvoiceLineItemsViewConfigParams {
    hasAnyAccrualLedgers: boolean;
    multiLedgerLedgers?: Ledger[];
    isSchedulesEnabled?: boolean;
    isClassesEnabled?: boolean;
}

export const createInvoiceLineItemsViewConfig = ({
    hasAnyAccrualLedgers,
    multiLedgerLedgers = [],
    isSchedulesEnabled = false,
    isClassesEnabled = false,
}: CreateInvoiceLineItemsViewConfigParams): ViewConfig<
    InvoiceLineItem,
    string,
    InvoiceSortValue
> => {
    const accrualLedgers = uniqueLedgersByBasisAndName(
        multiLedgerLedgers.filter(
            (ledger) => ledger.basis === AccountingLedgerBasis.accruals,
        ),
    );
    const isMultiLedgerEnabled = accrualLedgers.length > 0;

    return {
        columns: {
            [InvoiceLineItemColumn.LOCK]: {
                accessorKey: "id",
                name: "Lock",
                header: "",
                cell: LockCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 32,
                maxSize: 32,
            },
            [InvoiceLineItemColumn.SCHEDULE]: {
                accessorKey: "schedule.scheduleName",
                name: "Recognition schedule",
                header: "Recognition schedule",
                cell: ScheduleCell,
                ...SCHEDULE_COLUMN_SIZE,
                internal: !isSchedulesEnabled,
            },
            [InvoiceLineItemColumn.MATCH]: {
                accessorKey: "invoiceId",
                name: "Match",
                header: "Match",
                cell: MatchCell,
                minSize: 300,
                maxSize: 400,
            },
            [InvoiceLineItemColumn.DATE]: {
                accessorKey: "invoice.dateOnly",
                name: "Created on",
                header: "Created on",
                cell: DateCell,
                sort: {
                    asc: InvoiceSortValue.DATE_ASC,
                    desc: InvoiceSortValue.DATE_DESC,
                },
                ...DATE_COLUMN_SIZE,
            },
            [InvoiceLineItemColumn.ITEM]: {
                accessorKey: "nativeDetail.productOrService",
                name: "Item",
                header: "Item",
                cell: ItemCell,
                minSize: 180,
                maxSize: 240,
            },
            [InvoiceLineItemColumn.KIND]: {
                accessorKey: "kind",
                name: "Kind",
                header: "Kind",
                cell: KindCell,
                minSize: 100,
                maxSize: 140,
            },
            [InvoiceLineItemColumn.DESCRIPTION]: {
                accessorKey: "description",
                name: "Description",
                header: "Description",
                cell: DescriptionCell,
                minSize: 300,
                maxSize: 300,
            },
            ...(isClassesEnabled
                ? {
                      [InvoiceLineItemColumn.CLASS]: {
                          accessorKey: "classAssignments",
                          name: "Class",
                          header: "Class",
                          cell: ClassesCell,
                          minSize: 220,
                          maxSize: 220,
                      },
                  }
                : {}),
            [InvoiceLineItemColumn.STATUS]: {
                accessorKey: "invoice.status",
                name: "Status",
                header: "",
                cell: StatusCell,
                sort: {
                    asc: InvoiceSortValue.STATUS_ASC,
                    desc: InvoiceSortValue.STATUS_DESC,
                },
                minSize: 24,
                maxSize: 24,
                internal: !hasAnyAccrualLedgers,
            },
            [InvoiceLineItemColumn.EXTERNAL_STATUS]: {
                accessorKey: "invoice.externalStatus",
                name: "External status",
                header: "",
                cell: StatusCell,
                sort: {
                    asc: InvoiceSortValue.EXTERNAL_STATUS_ASC,
                    desc: InvoiceSortValue.EXTERNAL_STATUS_DESC,
                },
                minSize: 24,
                maxSize: 24,
            },
            [InvoiceLineItemColumn.CUSTOMER]: {
                accessorKey: "counterparty",
                name: "Customer",
                header: "Customer",
                cell: CustomerCell,
                sort: {
                    asc: InvoiceSortValue.COUNTERPARTY_ASC,
                    desc: InvoiceSortValue.COUNTERPARTY_DESC,
                },
                minSize: 200,
                maxSize: 400,
            },
            [InvoiceLineItemColumn.ACCOUNT]: {
                accessorKey: "invoice.financialAccount",
                name: "Account",
                header: "Account",
                cell: AccountCell,
                sort: {
                    asc: InvoiceSortValue.ACCOUNT_ASC,
                    desc: InvoiceSortValue.ACCOUNT_DESC,
                },
                minSize: 120,
                maxSize: 180,
            },
            [InvoiceLineItemColumn.ACCRUAL_ACCOUNT]: {
                accessorKey: "accountId",
                name: "Income account",
                header: "Income account",
                cell: AccrualLedgerCell,
                minSize: 120,
                maxSize: 260,
                internal: isMultiLedgerEnabled,
            },
            [InvoiceLineItemColumn.ACCOUNTS_RECEIVABLE]: {
                accessorKey: "accountsReceivableId",
                name: "Receivables account",
                header: "Receivables account",
                cell: AccountsReceivableCell,
                minSize: 120,
                maxSize: 260,
                internal: !hasAnyAccrualLedgers || isMultiLedgerEnabled,
            },
            ...Object.fromEntries(
                accrualLedgers.flatMap((ledger) => [
                    [
                        buildLedgerAccountColumnIdByName(ledger),
                        {
                            accessorKey:
                                buildLedgerAccountColumnIdByName(ledger),
                            name: ledger.name,
                            header: ledger.name,
                            cell: AccrualLedgerCell,
                            minSize: 120,
                            maxSize: 260,
                        },
                    ],
                    [
                        buildLedgerReceivableColumnIdByName(ledger),
                        {
                            accessorKey:
                                buildLedgerReceivableColumnIdByName(ledger),
                            name: `${ledger.name} receivables account`,
                            header: `${ledger.name} receivables account`,
                            cell: AccountsReceivableCell,
                            minSize: 120,
                            maxSize: 260,
                        },
                    ],
                ]),
            ),
            [InvoiceLineItemColumn.DUE_DATE]: {
                accessorKey: "invoice.dueDateOnly",
                name: "Due date",
                header: "Due date",
                cell: DateCell,
                sort: {
                    asc: InvoiceSortValue.DUE_DATE_ASC,
                    desc: InvoiceSortValue.DUE_DATE_DESC,
                },
                ...DATE_COLUMN_SIZE,
            },
            [InvoiceLineItemColumn.AMOUNT]: {
                accessorKey: "amount",
                name: "Amount",
                header: "Amount",
                cell: AmountCell,
                sort: {
                    asc: InvoiceSortValue.AMOUNT_ASC,
                    desc: InvoiceSortValue.AMOUNT_DESC,
                },
                ...AMOUNT_COLUMN_SIZE,
            },
            [InvoiceLineItemColumn.OPEN]: {
                accessorKey: "amount",
                name: "Open",
                header: "Open",
                cell: OpenAmountCell,
                sort: {
                    asc: InvoiceSortValue.OPEN_ASC,
                    desc: InvoiceSortValue.OPEN_DESC,
                },
                ...AMOUNT_COLUMN_SIZE,
            },
            [InvoiceLineItemColumn.INVOICE_NUMBER]: {
                accessorKey: "invoice.invoiceNumber",
                name: "Invoice number",
                header: "Invoice number",
                cell: InvoiceNumberCell,
                minSize: 120,
                maxSize: 120,
            },
            [InvoiceLineItemColumn.ENTITY]: {
                accessorKey: "invoice.financialAccount.entity",
                name: "Entity",
                header: "",
                cell: EntityCell,
                minSize: 48,
                maxSize: 48,
            },
            [InvoiceLineItemColumn.EXTERNAL_LINK]: {
                accessorKey: "invoice.id",
                name: "View",
                header: "",
                cell: ExternalLinkCell,
                immutable: true,
                hiddenFromSettings: true,
                minSize: 48,
                maxSize: 48,
            },
        },
    };
};
